import React, {useState, useRef, useEffect, useMemo} from 'react';
import {PanelGroup, Panel, PanelResizeHandle, ImperativePanelHandle} from 'react-resizable-panels';

import {makeReactContext} from 'lib/makeReactContext';

interface ResizableSplitterContext {
  isDragging: boolean;
}

const ResizableSplitterContext = makeReactContext<ResizableSplitterContext>(
  () => ({isDragging: false}),
  'ResizableSplitterContext'
);

function useResizableSplitterContext(): ResizableSplitterContext {
  return ResizableSplitterContext.useContext();
}

export interface ResizablePanelProps {
  // The index of the panel in the parent container (0, 1, 2, etc...)
  index: number;
  // If set to false, then the panel will not be rendered and none of the library code will be executed
  enabled?: boolean;
  children?: React.ReactNode;
  // Size in Percent, below this size the panel will auto collapse
  minSize?: number;
  // Size in Percent, this is the size the panel will open to whenever it is expanded
  defaultSize?: number;
  // If set to false, then the child underneath the panel wil not be rendered, and that panel will be
  // collapsed
  isRendered?: boolean;
  isCollapsed?: boolean;

  // Called when the panel is collapsed or expanded.
  // Note: These two methods must be idempotent, as they may be called multiple times due to the library and
  // possible side effects from the other panel collapsing or expanding
  onCollapse?: () => void;
  onExpand?: () => void;
}

/**
 * Module-level flag to determine if mock implementations should be used.
 * Set by mock() and restored by restore().
 */
let _isMocked = false;

/**
 * Test version of ResizablePanel that doesn't use react-resizable-panels.
 * This is needed because the library requires real DOM measurements that JSDOM doesn't provide.
 */
function ResizablePanelInTest({
  enabled,
  children,
  isRendered = true,
}: ResizablePanelProps): JSX.Element | null {
  if (enabled === false) {
    return <>{children}</>;
  }

  return <div className="split-panel">{isRendered && children}</div>;
}

/**
 * Production implementation of ResizablePanel using react-resizable-panels.
 */
function ResizablePanelImpl({
  index,
  enabled,
  children,
  minSize = 0,
  defaultSize = 50,
  isRendered = true,
  isCollapsed = false,
  onCollapse,
  onExpand,
}: ResizablePanelProps): JSX.Element | null {
  const [size, setSize] = useState<number>(-1);
  const [hasExpanded, setHasExpanded] = useState<boolean>(false);
  const hasExpandedRef = useRef<boolean>(hasExpanded);
  const {isDragging} = useResizableSplitterContext();
  const isDraggingRef = useRef(isDragging);
  const isRenderedRef = useRef<boolean | null>(null);
  const isCollapsedRef = useRef<boolean | null>(null);
  const panelRef = useRef<ImperativePanelHandle>(null);

  // When a panel is expanded or collapsed, call the appropriate callback
  // This doesn't directly use the component's onExpand/onCollapse props because they occur before the change
  // is visually rendered.  So moving this is a ref comparison allows it to trigger on the next render.
  useEffect(() => {
    if (hasExpanded !== hasExpandedRef.current) {
      if (hasExpanded) {
        onExpand?.();
      } else {
        onCollapse?.();
      }
      hasExpandedRef.current = hasExpanded;
    }
  }, [hasExpanded, onExpand, onCollapse]);

  // If you stop dragging a panel below its minimum size, then collapse it
  useEffect(() => {
    if (panelRef.current && isDragging === false && isDraggingRef.current === true) {
      if (size < minSize) {
        panelRef.current?.collapse();
      }
    }
    isDraggingRef.current = isDragging;
  }, [isDragging, size, minSize, onCollapse]);

  // If a panel starts rendering, then expand it to the default size
  // If a panel stops rendering, then collapse it
  useEffect(() => {
    const shouldBeOpen = isRendered && !isCollapsed;
    // If the size has not been set, then the panelRef methods will not be available yet.
    if (
      size > -1 &&
      panelRef.current &&
      (isRendered !== isRenderedRef.current || isCollapsed !== isCollapsedRef.current)
    ) {
      if (shouldBeOpen && size < minSize) {
        panelRef.current?.resize(defaultSize);
      } else if (!shouldBeOpen && size > 0) {
        panelRef.current?.collapse();
      }
      isRenderedRef.current = isRendered;
      isCollapsedRef.current = isCollapsed;
    }
  }, [isRendered, size, minSize, defaultSize, isCollapsed, onExpand, onCollapse]);

  if (enabled === false) {
    return <>{children}</>;
  }

  return (
    <Panel
      ref={panelRef}
      collapsible={true}
      minSize={1}
      defaultSize={defaultSize}
      order={index}
      id={`panel-${index}`}
      className="split-panel"
      onResize={(newSize) => {
        const newSizeInt = typeof newSize === 'number' ? newSize : parseInt(newSize, 10);
        if (size === 0 && newSizeInt > 0) {
          setHasExpanded(true);
        } else if (size > 0 && newSizeInt === 0) {
          setHasExpanded(false);
        }
        setSize(newSizeInt);
      }}
    >
      {onCollapse && size < minSize && size > 0 && <div className="split-drag-action-area" />}
      {isRendered && children}
    </Panel>
  );
}

export function ResizablePanel(props: ResizablePanelProps): JSX.Element | null {
  if (_isMocked) {
    return <ResizablePanelInTest {...props} />;
  }
  return <ResizablePanelImpl {...props} />;
}

export interface ResizableContainerProps {
  children: React.ReactNode;
  enabled?: boolean;
  style?: React.CSSProperties;
}

/**
 * Test version of ResizableContainer that renders simple divs instead of using react-resizable-panels.
 */
function ResizableContainerInTest({children, enabled}: ResizableContainerProps): JSX.Element {
  const context = useMemo(() => ({isDragging: false}), []);
  if (enabled === false) {
    return <>{children}</>;
  }
  return (
    <ResizableSplitterContext.Provider value={context}>
      <div data-panel-group={true} style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        {React.Children.map(children, (child, idx) => {
          const childReactElement = child as React.ReactElement;
          if (child) {
            return (
              <>
                {idx > 0 && (
                  <div
                    key={`splitter-${idx}`}
                    className="split-sash-content"
                    style={{
                      display: childReactElement.props?.isRendered === false ? 'none' : 'inherit',
                    }}
                  >
                    <div className="split-sash-inner" />
                  </div>
                )}
                {child}
              </>
            );
          }
          return false;
        })}
      </div>
    </ResizableSplitterContext.Provider>
  );
}

/**
 * Production implementation of ResizableContainer using react-resizable-panels.
 */
function ResizableContainerImpl({children, enabled, style}: ResizableContainerProps): JSX.Element {
  const [isDragging, setIsDragging] = useState(false);
  const context = useMemo(() => ({isDragging}), [isDragging]);
  if (enabled === false) {
    return <>{children}</>;
  }
  return (
    <ResizableSplitterContext.Provider value={context}>
      <PanelGroup direction="vertical" style={style}>
        {
          React.Children.map(children, (child, idx) => {
            const childReactElement = child as React.ReactElement;
            if (child) {
              return (
                <>
                  {idx > 0 && (
                    <PanelResizeHandle
                      key={`splitter-${idx}`}
                      className="split-sash-content"
                      style={{
                        display: childReactElement.props?.isRendered === false ? 'none' : 'inherit',
                      }}
                      onDragging={(isDragging) => {
                        setIsDragging(isDragging);
                      }}
                    >
                      <div className="split-sash-inner" />
                    </PanelResizeHandle>
                  )}
                  {child}
                </>
              );
            }
            return false;
          }) as Array<JSX.Element>
        }
      </PanelGroup>
    </ResizableSplitterContext.Provider>
  );
}

export function ResizableContainer(props: ResizableContainerProps): JSX.Element {
  if (_isMocked) {
    return <ResizableContainerInTest {...props} />;
  }
  return <ResizableContainerImpl {...props} />;
}

/**
 * Enable mock implementations for testing.
 * This replaces react-resizable-panels with simple divs that work in JSDOM.
 * Must call restore() in afterEach to cleanup.
 */
export function mock(): void {
  _isMocked = true;
}

/**
 * Restore production implementations after testing.
 */
export function restore(): void {
  _isMocked = false;
}
